import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@reckon-web/button';
import { Box } from '@reckon-web/box';
import { Flex } from '@reckon-web/flex';
import { SendIcon } from '@reckon-web/icon';
import { FieldLabel, FieldMessage } from '@reckon-web/field';
import { useToasts } from '@reckon-web/toast';
import { makeId, useId } from '@reckon-web/utils';

import type { Job } from '../../../hooks/use-query-job';
import {
  JobAddCommentVariables,
  useJobAddComment,
} from '../../../data/useJobAddComment';

// Weird dynamic import because
// Tribute doesn't work in SSR https://github.com/zurb/tribute/issues/622
// remove this dynamic import when the issue above is resolved (and a release is made with the fix in it)
const CommentTextEditor = dynamic(
  // ts ignore because this dynamic import is causing typescript to give the worlds second longest error message
  // about types not matching
  //@ts-ignore
  () =>
    import('../../common/comment-text-editor').then(
      (mod) => mod.CommentTextEditor
    ),
  { ssr: false }
);

export const AddComment: React.FC<{
  job: Job;
  testId?: string;
}> = ({ job, testId = `add-comment` }) => {
  const [comment, setComment] = useState<string>();
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>();
  const [addComment] = useJobAddComment();
  const { addToast } = useToasts();
  const id = useId();
  const inputId = makeId('input', id);
  const labelId = makeId('label', id);
  const messageId = makeId('message', id);

  // if they click back into the comment box, remove the invalid
  const handleFocus = () => {
    setIsInvalid(false);
  };

  const submit = async () => {
    if (!comment) {
      // they've tried to save without a comment, now is when we want to show the validation
      setIsInvalid(true);
      return;
    }
    setIsInvalid(false);

    setIsSaving(true);
    const input: JobAddCommentVariables = {
      cloudId: uuidv4(),
      comment: comment || '',
      jobId: job.cloudId || '',
    };
    const gqlResult = await addComment({ variables: input });
    if (
      gqlResult &&
      gqlResult.data &&
      gqlResult.data.jobAddComment &&
      (!gqlResult.data.jobAddComment.validationMessage ||
        gqlResult.data.jobAddComment.validationMessage.length === 0)
    ) {
      // happy path
      // empty the comment so they can go again
      setComment('');
    } else {
      // sad path. Could see if there was a validation error for the comment field
      // but we'll just generic toast and move on
      addToast({
        title: `Comment not created`,
        message: `Workflow failed to create the comment. Try again or contact support.`,
        tone: 'critical',
      });
    }
    setIsSaving(false);
  };

  return (
    <Flex gap="medium">
      <Box flex="1 1 100%">
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          visible={false}
          data-testid={makeId(testId, 'label')}
        >
          Comment
        </FieldLabel>
        <CommentTextEditor
          value={comment}
          invalid={isInvalid}
          onFocus={handleFocus}
          onChange={(value) => setComment(value)}
          placeholder="Add a comment..."
          aria-describedby={isInvalid ? messageId : undefined}
          id={inputId}
          labelId={labelId}
          data-testid={makeId(testId, 'comment-text-editor')}
        />
        {isInvalid && (
          <FieldMessage id={messageId} data-testid={makeId(testId, 'message')}>
            Comment cannot be blank
          </FieldMessage>
        )}
      </Box>
      <Box alignSelf="flex-end">
        <IconButton
          weight="subtle"
          icon={SendIcon}
          label="Add comment"
          disabled={isSaving}
          onClick={() => submit()}
          data-testid={makeId(testId, 'icon-button')}
        />
      </Box>
    </Flex>
  );
};
